import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as yup from "yup";
import "bootstrap/dist/css/bootstrap.min.css";

// ✅ Validation Schema
const validationSchema = yup.object().shape({
  // Personal Information
  firstName: yup.string().required("First name is required"),
  middleName: yup.string(), // Optional
  lastName: yup.string().required("Last name is required"),
  username: yup.string().min(5, "Min 5 characters").required("Username is required"),
  email: yup.string().email("Invalid email format").required("Email is required"),
  phoneNumber: yup.string().matches(/^[0-9]{10}$/, "Must be exactly 10 digits").required("Phone is required"),
  dob: yup.date().required("Date of birth required"),
  gender: yup.string().oneOf(["male", "female", "other"], "Select a valid gender").required("Gender required"),

  // Account Details
  password: yup
    .string()
    .min(8, "Min 8 characters")
    .matches(/\d/, "Must contain a number")
    .matches(/[A-Z]/, "Must have an uppercase letter")
    .required("Password required"),
  confirmPassword: yup.string().oneOf([yup.ref("password"), null], "Passwords must match").required("Confirm password"),

  // Address
  country: yup.string().required("Country required"),
  city: yup.string().required("City required"),
  address: yup.string().required("Address required"),
  zipCode: yup.string().matches(/^\d{5}$/, "Must be 5 digits").required("Zip Code required"),

  // Security
  securityQuestion: yup.string().required("Security question required"),
  securityAnswer: yup.string().min(3, "Min 3 chars").required("Answer required"),
  terms: yup.boolean().oneOf([true], "Must accept terms"),

  // Optional Fields
  linkedin: yup.string().url("Must be a valid URL"),
  twitter: yup.string().url("Must be a valid URL"),
  bio: yup.string().max(300, "Max 300 characters"),
});

const ComplexForm = () => {
  const [message, setMessage] = useState({ text: "", type: "" });

  return (
    <div className="container" id="form-container" style={{ maxWidth: "900px", margin: "auto", marginTop: "30px" }}>
      <h3 className="text-center mb-4">User Registration</h3>

      {message.text && <div className={`alert alert-${message.type}`}>{message.text}</div>}

      <Formik
        initialValues={{
          firstName: "",
          middleName: "",
          lastName: "",
          username: "",
          email: "",
          phoneNumber: "",
          password: "",
          confirmPassword: "",
          dob: "",
          gender: "",
          country: "",
          city: "",
          address: "",
          zipCode: "",
          securityQuestion: "",
          securityAnswer: "",
          terms: false,
          linkedin: "",
          twitter: "",
          bio: "",
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          setMessage({ text: "", type: "" });

          try {
            await new Promise((resolve) => setTimeout(resolve, 2000)); // Simulated delay
            setMessage({ text: "Form submitted successfully!", type: "success" });
            resetForm();
          } catch (error) {
            setMessage({ text: "Submission failed!", type: "danger" });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ isSubmitting }) => (
          <Form id="complex-form">
            <div className="row">
              {/* 🟢 Personal Info */}
              <div className="col-md-12">
                <h5>🧑 Personal Information</h5>
              </div>

              {[
                ["First Name *", "firstName"],
                ["Middle Name", "middleName"], // Optional
                ["Last Name *", "lastName"],
                ["Username *", "username"],
                ["Email *", "email"],
                ["Phone Number *", "phoneNumber"],
                ["Date of Birth *", "dob"],
              ].map(([label, name]) => (
                <div className="col-md-6 col-sm-12 mb-3" key={name}>
                  <label htmlFor={name}>{label}</label>
                  <Field type="text" id={name} name={name} className="form-control" />
                  <ErrorMessage name={name} component="small" className="text-danger" />
                </div>
              ))}

              {/* Gender Dropdown */}
              <div className="col-md-6 col-sm-12 mb-3">
                <label htmlFor="gender">Gender *</label>
                <Field as="select" id="gender" name="gender" className="form-control">
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </Field>
                <ErrorMessage name="gender" component="small" className="text-danger" />
              </div>

              {/* 🟠 Account Details */}
              <div className="col-md-12">
                <h5>🔑 Account Details</h5>
              </div>

              {[
                ["Password *", "password"],
                ["Confirm Password *", "confirmPassword"],
              ].map(([label, name]) => (
                <div className="col-md-6 col-sm-12 mb-3" key={name}>
                  <label htmlFor={name}>{label}</label>
                  <Field type="password" id={name} name={name} className="form-control" />
                  <ErrorMessage name={name} component="small" className="text-danger" />
                </div>
              ))}

              {/* 🔵 Address Details */}
              <div className="col-md-12">
                <h5>🏠 Address</h5>
              </div>

              {[
                ["Country *", "country"],
                ["City *", "city"],
                ["Address *", "address"],
                ["Zip Code *", "zipCode"],
              ].map(([label, name]) => (
                <div className="col-md-6 col-sm-12 mb-3" key={name}>
                  <label htmlFor={name}>{label}</label>
                  <Field type="text" id={name} name={name} className="form-control" />
                  <ErrorMessage name={name} component="small" className="text-danger" />
                </div>
              ))}

              {/* 🟡 Security */}
              <div className="col-md-12">
                <h5>🔒 Security</h5>
              </div>

              {[
                ["Security Question *", "securityQuestion"],
                ["Security Answer *", "securityAnswer"],
              ].map(([label, name]) => (
                <div className="col-md-6 col-sm-12 mb-3" key={name}>
                  <label htmlFor={name}>{label}</label>
                  <Field type="text" id={name} name={name} className="form-control" />
                  <ErrorMessage name={name} component="small" className="text-danger" />
                </div>
              ))}

              {/* Terms & Conditions */}
              <div className="col-md-12">
                <Field type="checkbox" id="terms" name="terms" className="form-check-input" />
                <label htmlFor="terms"> I accept the terms and conditions *</label>
                <ErrorMessage name="terms" component="small" className="text-danger" />
              </div>

              {/* Submit Button */}
              <div className="col-md-12 text-center mt-3">
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? "Processing..." : "Submit"}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ComplexForm;
